import type {
    ComposableOptionsBase
} from '~/types';
import {
    useLogging,
    useCustomAuth,
    useCmsApiUtils,
    useSessionId
} from '~/composables';


export interface UseCustomFetchOptions extends ComposableOptionsBase {
    apiName?: Nilish<string>;
    customHeaders?: Record<string, string>;
    rawHeaders?: Record<string, string>;
}
export function useCustomFetch(options: Partial<UseCustomFetchOptions> = {}) {
    const {
        refreshTokens,
        isTokenExpired,
        isLoggedIn,
        sessionObj,
        getToken
    } = useCustomAuth();
    const {
        sessionIdHeader,
        baseURL,
        browserBaseURL,
        getCommonHeaders,
        getCustomHeaders,
        orgIdHeader
    } = useCmsApiUtils({ siteContext: options.siteContext });
    const {
        logSentryError
    } = useLogging();
    const {
        getOrGenSessionId
    } = useSessionId();

    const origUa: Nilish<string> = import.meta.server ? useRequestHeader('user-agent') : null;

    const persistentCustomHeadersObj = getCustomHeaders(options.customHeaders ?? {});
    const persistentRawHeaders = options.rawHeaders ?? {};
    const persistentHeaders = {
        ...persistentCustomHeadersObj,
        ...persistentRawHeaders
    };

    return $fetch.create({
        headers: persistentHeaders,
        async onRequest({ options: reqOpts }) {
            // console.time('custom-fetch');
            reqOpts.headers = new Headers(reqOpts.headers);
            let siteCurrentOrgUid = '';

            if (isLoggedIn.value) {
                if (isTokenExpired()) {
                    await refreshTokens();
                }

                const token = getToken();

                if (token) {
                    reqOpts.headers.set('Authorization', `Bearer ${token}`);
                }

                siteCurrentOrgUid = sessionObj.value?.membershipChoices.find((o) => !!o.isSelected)?.membershipUid ?? '';
            }

            // Sets the X-Requested-With header
            reqOpts.headers.set('X-Requested-With', 'XMLHttpRequest');

            // Sets the baseUrl
            reqOpts.baseURL = import.meta.server ? baseURL : browserBaseURL;

            // Org ID / Org Membership Context

            const mergedHeaders = {
                ...getCommonHeaders(),
                [orgIdHeader]: siteCurrentOrgUid
            };

            // Add common headers
            for (const [key, value] of Object.entries(mergedHeaders)) {
                reqOpts.headers.set(String(key), String(value));
            }

            // Session
            const sessionIdHeaderVal = await getOrGenSessionId();
            reqOpts.headers.set(sessionIdHeader, sessionIdHeaderVal);

            if (origUa) {
                reqOpts.headers.set('user-agent', origUa);
            }

            // console.timeEnd('custom-fetch');
        },
        onRequestError({ request, options, error }) {
            const url = import.meta.server ? baseURL : browserBaseURL;

            if (
                request.toString().startsWith(url) &&
                error.cause?.toString().includes('connect ECONNREFUSED')
            ) {
                throw createError({ statusCode: 503, statusMessage: 'Site Offline', fatal: true });
            }
        },
        async onResponseError({ response }) {
            if (!import.meta.client) {
                return;
            }

            const { url, status, statusText } = response;

            // Only log 5xx errors
            if (status < 500) return;

            logSentryError(
                new Error('custom-fetch-error'),
                {
                    tags: {
                        module: 'custom-fetch',
                        section: 'all'
                    },
                    extra: {
                        url,
                        status,
                        statusText,
                        event_id: '0'
                    },
                    level: 'error'
                }
            );
        }
    });
}
